import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { orgDashboardApiIsomorphicFetchOrg } from '#/modules/orgDashboard/api/orgDashboardApiFetchOrg';
import { orgDashboardApiIsomorphicFetchOptionsForPosting } from '#/modules/orgDashboard/api/orgDashboardApiIsomorphicFetchOptionsForPosting';
import {
  orgDashboardManageListingsRoute,
  orgDashboardPostAListingRoute,
} from '#/modules/orgDashboard/routing/orgDashboard.routes';
import type { OrgDashboardOrgSubLayoutHandleProps } from '#/modules/orgDashboard/sub-layouts/Org/OrgDashboardOrgSubLayoutHandleProps';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';

import { OrgDashboardPostAListingPageLoadable } from './OrgDashboardPostAListingPageLoadable';

export const handle = {
  variant: 'base',
  orgDashboardSubLayoutVariant: 'base',
  darkBackground: true,
} satisfies LayoutHandleProps & OrgDashboardOrgSubLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { orgId },
}: HorizonAppRouteLoaderArgs<typeof orgDashboardPostAListingRoute>) {
  const [{ org }, optionsForPosting] = await Promise.all([
    orgDashboardApiIsomorphicFetchOrg({ request, context })(orgId),
    orgDashboardApiIsomorphicFetchOptionsForPosting({ request, context })(
      orgId,
    ),
    OrgDashboardPostAListingPageLoadable.load(),
  ]);

  if (!org.isAllowedToPost || org.isFrozen) {
    return idealistRedirect({
      request,
      context,
      to: orgDashboardManageListingsRoute.with({ orgId: org.id }),
    });
  }

  return { optionsForPosting };
}

export default function OrgDashboardPostAListingHorizonRoute({
  loaderData: { optionsForPosting },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  return (
    <RouteModule title={i18n('Post A Listing')}>
      <OrgDashboardPostAListingPageLoadable
        optionsForPosting={optionsForPosting}
      />
    </RouteModule>
  );
}
